import { Icon } from "@iconify/react";
import { BillingInfo } from "./types";
import { formatDay, formatMoney } from "@/utils/billingFormat";
import { ButtonSpinner } from "@/components/ui/Button";
import { PrimaryCell, StatusText } from "@/components/ui/TableCells";
import { humanize } from "@/lib/status";

// Invoice states on the shared status look (icon + word in the status colour).
const STATUS: Record<string, { text: string; as: string }> = {
  paid: { text: "Paid", as: "completed" },
  open: { text: "Due", as: "partial" },
  uncollectible: { text: "Unpaid", as: "failed" },
  void: { text: "Void", as: "cancelled" },
};

export default function Invoices({ invoices, onOpenPortal, opening }: { invoices: BillingInfo["invoices"]; onOpenPortal: () => void; opening: boolean }) {
  return (
    <div className="bg-surface border border-line rounded-card shadow-card">
      {invoices.length === 0 ? (
        <p className="px-5 py-6 text-sm text-fg-muted">No invoices yet.</p>
      ) : (
        <ul className="divide-y divide-line">
          {invoices.map(inv => {
            const st = STATUS[inv.status ?? ""] ?? { text: inv.status ? humanize(inv.status) : "Unknown", as: "queued" };
            return (
              <li key={inv.id} className="flex min-h-[68px] items-center gap-3 px-4 py-3 sm:gap-4 sm:px-5">
                <PrimaryCell
                  icon="lucide:receipt-text"
                  className="flex-1"
                  title={<span className="tabular-nums">{formatMoney(inv.amountCents, inv.currency)}</span>}
                  titleText={formatMoney(inv.amountCents, inv.currency)}
                  subtitle={formatDay(inv.date)}
                />
                <StatusText status={st.as} label={st.text} className="shrink-0 sm:w-28" />
                {inv.url ? (
                  <a href={inv.url} target="_blank" rel="noopener noreferrer" className="shrink-0 inline-flex min-h-8 items-center gap-1 text-sm text-primary-text hover:underline" aria-label={`View invoice from ${formatDay(inv.date)}`}>
                    View <Icon icon="mdi:open-in-new" className="text-xs" aria-hidden="true" />
                  </a>
                ) : (
                  <span className="shrink-0 w-12" />
                )}
              </li>
            );
          })}
        </ul>
      )}
      <div className="px-5 py-3 border-t border-line">
        <button type="button" onClick={onOpenPortal} disabled={opening} className="inline-flex items-center gap-1.5 text-sm font-medium text-primary-text hover:underline cursor-pointer disabled:opacity-60">
          {opening && <ButtonSpinner />}
          All invoices and billing details
          <Icon icon="mdi:open-in-new" className="text-xs" />
        </button>
      </div>
    </div>
  );
}
